import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Subscription,
  startWith,
  pairwise,
  take,
} from 'rxjs';
import { SpaceBoardsService } from 'src/app/services/space-boards.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import {
  FollowModeRepositoryService,
  ISpaceFollowMode,
} from 'src/app/state/temporary-metadata/follow-mode/follow-mode-repository.service';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { UserEventType } from 'src/app/models/session-sync';
import { PresenceRepository } from 'src/app/state/presence.repository';
import { TranslateService } from '@ngx-translate/core';
import { INFOS } from 'src/app/common/utils/notification-constants';
import { ToasterPopupStyle } from 'src/app/ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { IconBackground } from 'src/app/ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { InspectModeRepository } from 'src/app/state/inspect-mode-repository.service';
import { RealtimeSpaceService } from '../../realtime-space.service';
import {
  LeaderFollowModesSharedUtilsService,
  SendingAndListeningForEventsOpts,
} from '../leader-follow-modes-shared-utils.service';
import { SessionsSyncService } from '../../sessions-sync.service';
import { SPECTATOR_SUFFIX, UserService } from '../../user.service';
import { SpaceLeaderModeService } from '../../space-leader-mode.service';
import { ContextTypeEnum, PresenceData, PresenceType } from '../../presence_v2.service';
import { SessionSharedDataService } from '../../session-shared-data.service';
import {
  NotificationActionButton,
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationDataBuilder,
} from '../../notification-toaster.service';
import { SessionsVptService } from '../../sessions-vpt.service';
import { TelemetryService } from '../../telemetry.service';
import { isObserverTabId } from '../../observer-mode.utils';

interface IImplicitCloseReason {
  sessionViewChanged?: boolean;
  fullScreenIDChanged?: boolean;
  expandedFullScreenUserChanged?: boolean;
  expandedFullScreenStateChanged?: boolean;
  expandedFullScreentrackChanged?: boolean;
  boardChanged?: boolean;
  vptTransformChanged?: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class FollowModeUiService {
  private followModeStateSubscription?: Subscription;
  private followersSubscription?: Subscription;
  private framesStateSubscription?: Subscription;
  private joiningCallSubscription?: Subscription;
  private followingLocationSubscription?: Subscription;
  private followingUpdatesSubscription?: Subscription;
  private deactivateFollowModeSubscriptions?: Subscription[];
  private leaderModeSubscription?: Subscription;
  private followingUserPresenceSubscription?: Subscription;
  private followModeEnabledSubscription?: Subscription;
  private newTabNotificationSubscription?: Subscription;
  private implicitCloseSubscription: Subscription[] = [];
  private firstUpdateSubscription?: Subscription;

  private currentFollowModeState: ISpaceFollowMode | undefined = undefined;
  private previousFollowModeState: ISpaceFollowMode | undefined = undefined;

  public isBeingFollowed$ = this.followModeRepositoryService.spaceFollowMode$.pipe(
    map(() => this.isBeingFollowed()),
    distinctUntilChanged(),
  );

  public isFollowing$ = this.followModeRepositoryService.spaceFollowMode$.pipe(
    map(() => this.isFollowing()),
    distinctUntilChanged(),
  );

  public isPendingFollowing$ = this.followModeRepositoryService.spaceFollowMode$.pipe(
    map(() => this.isPendingFollowing()),
    distinctUntilChanged(),
  );

  public followingTabID$ = this.followModeRepositoryService.spaceFollowMode$.pipe(
    map((followModeState) => followModeState.followingTabID),
    distinctUntilChanged(),
  );

  public followingID$ = this.followModeRepositoryService.spaceFollowMode$.pipe(
    map((followModeState) => followModeState.followingID),
    distinctUntilChanged(),
  );

  public readonly followedUser$ = this.followingID$.pipe(
    map((followingID) =>
      this.spaceRepo.activeSpace?.populatedUsers.find((user) => user._id === followingID),
    ),
  );

  public followers$ = this.followModeRepositoryService.spaceFollowMode$.pipe(
    map((followModeState) =>
      (followModeState.followers || []).filter(
        (userId) => !userId.includes(SPECTATOR_SUFFIX) && !isObserverTabId(userId),
      ),
    ),
  );

  private _followingUserOpenedTabs$ = new BehaviorSubject<Set<string>>(new Set<string>());
  public followingUserOpenedTabs$ = this._followingUserOpenedTabs$.asObservable();

  public isFollowModeActive$ = combineLatest([
    this.isFollowing$,
    this.isPendingFollowing$,
    this.isBeingFollowed$,
  ]).pipe(
    map(
      ([isFollowing, isPendingFollowing, isBeingFollowed]) =>
        isFollowing || isPendingFollowing || isBeingFollowed,
    ),
  );

  public isFollowingOrPendingFollowing$ = combineLatest([
    this.isFollowing$,
    this.isPendingFollowing$,
  ]).pipe(map(([isFollowing, isPendingFollowing]) => isFollowing || isPendingFollowing));

  private _isFollowModeEnabled$ = new BehaviorSubject<boolean>(false);
  public isFollowModeEnabled$ = this._isFollowModeEnabled$.asObservable();

  private _followModeDisabledText$ = new BehaviorSubject<string>('');
  public followModeDisabledText$ = this._followModeDisabledText$.asObservable();

  public followingUserPointerData$ = combineLatest([
    this.followingID$.pipe(distinctUntilChanged(), filter(Boolean)),
    this.sessionSharedDataService.userPointerDataEvent.pipe(distinctUntilChanged()),
  ]).pipe(
    map(([targetUserID, userPointer]) => {
      const userPointerData =
        userPointer?.user === targetUserID
          ? userPointer?.pointerColor
          : this.sessionSharedDataService.usersPointerData.get(targetUserID);

      return {
        pointerColor: userPointerData || '',
      };
    }),
    distinctUntilChanged(),
  );

  constructor(
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceBoardsService: SpaceBoardsService,
    private spaceRepo: SpaceRepository,
    private followModeRepositoryService: FollowModeRepositoryService,
    private leaderFollowModesSharedUtilsService: LeaderFollowModesSharedUtilsService,
    private providerState: ProviderStateService,
    private sessionSyncService: SessionsSyncService,
    private userService: UserService,
    private spaceLeaderModeService: SpaceLeaderModeService,
    private presenceRepository: PresenceRepository,
    private translateService: TranslateService,
    private sessionSharedDataService: SessionSharedDataService,
    private notificationToasterService: NotificationToasterService,
    private sessionsVptService: SessionsVptService,
    private telemetryService: TelemetryService,
    private inspectModeRepository: InspectModeRepository,
  ) {}

  initService() {
    this.setupFollowModeStateListener();
    this.handleStopLeadingListener();
    this.setupJoiningOrLeavingCallListener();
    this.setupDeactivateFollowModeListener();
    this.setupLeaderModeListener();
    this.setupFollowModeEnabledListener();
  }

  private setupFollowModeStateListener() {
    this.followModeStateSubscription?.unsubscribe();
    this.followModeStateSubscription = this.followModeRepositoryService.spaceFollowMode$.subscribe(
      (newFollowModeState) => {
        this.previousFollowModeState = this.currentFollowModeState;
        this.currentFollowModeState = newFollowModeState;
        this.setupVPTEvents(this.previousFollowModeState, this.currentFollowModeState);
      },
    );
  }

  private setupVPTEvents(
    oldFollowModeState: ISpaceFollowMode | undefined,
    newFollowModeState: ISpaceFollowMode | undefined,
  ) {
    const oldFollowers = oldFollowModeState?.followers;
    const newFollowers = newFollowModeState?.followers;
    const sameFollowingTabID =
      oldFollowModeState?.followingTabID === newFollowModeState?.followingTabID;

    const stopSendingVptEvents: boolean =
      !!oldFollowers &&
      oldFollowers.length > 0 &&
      !newFollowers?.length &&
      !this.spaceLeaderModeService.currentUserIsLeader();
    const startSendingVptEvents: boolean =
      !!newFollowers && newFollowers.length > 0 && !oldFollowers?.length;

    // we need the startLeading to be called before sending any events
    // so that is being followed is true and the event can be sent
    if (startSendingVptEvents) {
      this.startLeading();
    }

    const startListeningForVptEvents: boolean =
      !sameFollowingTabID &&
      !!newFollowModeState?.followingTabID &&
      !!newFollowModeState?.followingID;
    const stopListeningForVptEvents: boolean =
      !sameFollowingTabID &&
      !newFollowModeState?.followingTabID &&
      !newFollowModeState?.followingID &&
      oldFollowModeState?.followingID !==
        this.spaceLeaderModeService.getSpaceLeaderModeStateUi().currentLeaderUid;

    const handleVptLock: boolean = false;

    const followingUserID: string =
      newFollowModeState?.followingID || oldFollowModeState?.followingID || '';

    const stateIsNotChanged: boolean =
      sameFollowingTabID && !stopSendingVptEvents && !startSendingVptEvents;

    const sendingAndListeningForEventsOpts: SendingAndListeningForEventsOpts = {
      stateIsNotChanged,
      stopSendingVptEvents,
      startSendingVptEvents,
      startListeningForVptEvents,
      stopListeningForVptEvents,
      handleVptLock,
      followingUserID,
    };

    this.leaderFollowModesSharedUtilsService.handleSendingAndListeningForEvents(
      sendingAndListeningForEventsOpts,
    );
  }

  private handleStopLeadingListener() {
    this.followersSubscription?.unsubscribe();
    this.followersSubscription = this.isBeingFollowed$
      .pipe(startWith(false), pairwise())
      .subscribe(([prevIsBeingFollowed, currIsBeingFollowed]) => {
        if (!currIsBeingFollowed && prevIsBeingFollowed) {
          this.stopLeading();
        }
      });
  }

  startFollowing(userID: string, tabID?: string) {
    this.telemetryService.logBasicEvent('[FollowMode] start following');
    console.log('[FollowMode] start following');
    this.detectFollowingUserOpenedTabs(userID);

    if (!tabID) {
      tabID = this.selectFollowingTab();
      if (!tabID) {
        return;
      }
    }

    if (!this._followingUserOpenedTabs$.value.has(tabID)) {
      return;
    }

    this.realtimeSpaceService.service.modifyTemporaryUserMetadata({
      followingID: userID,
      followingTabID: tabID,
      isFollowing: true,
    });
    this.setupFollowingListeners();

    this.logWhenReceivingFirstUpdate();
  }

  private logWhenReceivingFirstUpdate() {
    this.firstUpdateSubscription?.unsubscribe();
    this.firstUpdateSubscription = this.isFollowing$
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        console.log('[FollowMode] received first update');
      });
  }

  private detectFollowingUserOpenedTabs(userID: string) {
    const currentPresenceData =
      this.presenceRepository.getBreakoutRoomPresenceUsersData(
        this.spaceRepo.activeSpaceId || '',
        PresenceType.SPACE_PRESENCE,
        ContextTypeEnum.SPACE,
        this.spaceRepo.activeSpaceCurrentRoomUid,
      ) || [];

    const followingUserOpenedTabs = this.getOpenedTabsForUser(userID, currentPresenceData);
    this._followingUserOpenedTabs$.next(followingUserOpenedTabs);
  }

  private selectFollowingTab(): string {
    const followingUserOpenedTabs = this._followingUserOpenedTabs$.value;
    return this.getMaxTabID(followingUserOpenedTabs);
  }

  getOpenedTabsForUser(userID: string, roomPresence: Array<PresenceData>): Set<string> {
    const followingUserOpenedTabs = roomPresence
      .filter((presence) => presence.userId === userID)
      .map((presence) => presence.uniqueHash);

    return new Set<string>(followingUserOpenedTabs);
  }

  private getMaxTabID(tabs: Set<string>): string {
    if (tabs.size === 0) {
      return '';
    }
    const sortedTabs = Array.from(tabs).sort();
    return sortedTabs[sortedTabs.length - 1];
  }

  private setupFollowingListeners() {
    this.setupFollowingUpdatesListener();
    this.setupFramesStateListener();
    this.setupFollowingLocationListener();
    this.setupFollowingUserPresenceListener();
    this.setupImplicitCloseListener();
  }

  private setupFollowingUpdatesListener() {
    this.followingUpdatesSubscription?.unsubscribe();
    this.followingUpdatesSubscription = this.followModeRepositoryService.spaceFollowMode$.subscribe(
      (newFollowModeState) => {
        // to break a cycle when the user is following and  leader mode is activated
        if (
          this.spaceLeaderModeService.getSpaceLeaderModeStateUi().currentLeaderUid ||
          !this.isFollowing()
        ) {
          return;
        }

        const {
          followingSpaceView,
          followingExpandAndFullScreenChange,
          followingSessionViewMetaData,
          followingTabID,
          followingFrameID,
          followingInitLocation,
        } = newFollowModeState || {};

        this.leaderFollowModesSharedUtilsService.selectFollowingSessionView(
          followingSpaceView,
          followingExpandAndFullScreenChange,
          followingSessionViewMetaData,
        );
        if (followingFrameID) {
          this.leaderFollowModesSharedUtilsService.selectFollowingFrame(followingFrameID);
        }
        if (followingInitLocation) {
          const applyCentering = followingTabID !== this.userService.userUniqueHash;
          this.leaderFollowModesSharedUtilsService.updateVptUsingFollowingLocation(
            followingInitLocation,
            applyCentering,
          );
        }
      },
    );
  }

  private setupFramesStateListener() {
    this.framesStateSubscription?.unsubscribe();
    this.framesStateSubscription = this.spaceBoardsService.activeSpaceCurrentRoomFrames$.subscribe(
      () => {
        if (!this.spaceRepo.activeSpace) {
          return;
        }
        if (this.currentFollowModeState?.followingFrameID) {
          this.leaderFollowModesSharedUtilsService.selectFollowingFrame(
            this.currentFollowModeState?.followingFrameID,
          );
        }
      },
    );
  }

  private setupJoiningOrLeavingCallListener() {
    this.joiningCallSubscription?.unsubscribe();
    this.joiningCallSubscription = this.providerState.callConnected$.subscribe(
      (isCallConnected) => {
        this.leaderFollowModesSharedUtilsService.handleDetectJoinedOrLeftCall(
          isCallConnected,
          this.currentFollowModeState?.followingSpaceView,
          this.currentFollowModeState?.followingExpandAndFullScreenChange,
          this.currentFollowModeState?.followingSessionViewMetaData,
          !!this.currentFollowModeState?.followers?.length,
        );
      },
    );
  }

  private setupDeactivateFollowModeListener() {
    this.cleanUpDeactivateFollowModeListeners();
    this.deactivateFollowModeSubscriptions?.push(
      this.spaceRepo.activeSpaceCurrentRoomUid$
        .pipe(distinctUntilChanged(), filter(Boolean))
        .subscribe(() => {
          this.deactivateFollowMode();
        }),
    );

    this.deactivateFollowModeSubscriptions?.push(
      this.sessionSharedDataService.hasUserLeftSpace$.pipe(filter(Boolean)).subscribe(() => {
        this.deactivateFollowMode();
      }),
    );
  }

  public deactivateFollowMode() {
    if (this.isFollowingOrPendingFollowing()) {
      this.stopFollowing();
    }
    if (this.isBeingFollowed()) {
      this.stopLeading();
    }
  }

  public isFollowingOrPendingFollowing(): boolean {
    return this.isFollowing() || this.isPendingFollowing();
  }

  public isFollowing(): boolean {
    const currFollowModeState = this.followModeRepositoryService.getCurrentFollowModeState();
    return (
      !!currFollowModeState?.followingID &&
      !!currFollowModeState?.followingTabID &&
      !!currFollowModeState?.followingSpaceView
    );
  }

  public isPendingFollowing(): boolean {
    const currFollowModeState = this.followModeRepositoryService.getCurrentFollowModeState();
    const result =
      !!currFollowModeState?.followingID &&
      !!currFollowModeState?.followingTabID &&
      !currFollowModeState?.followingSpaceView;
    return result;
  }

  public isBeingFollowed(): boolean {
    const currFollowModeState = this.followModeRepositoryService.getCurrentFollowModeState();
    const followers = currFollowModeState?.followers;
    return !!followers && followers.length > 0;
  }

  public getCurrentFollowingUserID(): string {
    return this.currentFollowModeState?.followingID || '';
  }

  public getCurrentFollowingTabID(): string {
    return this.currentFollowModeState?.followingTabID || '';
  }

  private setupLeaderModeListener() {
    this.leaderModeSubscription?.unsubscribe();
    this.leaderModeSubscription = this.spaceLeaderModeService.leaderModeActive$
      .pipe(
        distinctUntilChanged(),
        filter((isLeaderModeActive) => isLeaderModeActive),
      )
      .subscribe(() => {
        if (this.isFollowingOrPendingFollowing()) {
          this.stopFollowing();
        }
      });
  }

  // will be used if we introduced more cases to disable follow mode
  private setupFollowModeEnabledListener() {
    this.followModeEnabledSubscription?.unsubscribe();
    this.followModeEnabledSubscription = this.spaceLeaderModeService.leaderModeActive$.subscribe(
      (isLeaderModeActive) => {
        if (isLeaderModeActive) {
          this._isFollowModeEnabled$.next(false);
          this._followModeDisabledText$.next(
            this.translateService.instant('You can not follow others when leader mode is active'),
          );
          return;
        }
        this._isFollowModeEnabled$.next(true);
        this._followModeDisabledText$.next('');
      },
    );
  }

  private setupFollowingLocationListener() {
    this.followingLocationSubscription?.unsubscribe();
    this.followingLocationSubscription = this.sessionSyncService.userEvents$
      .pipe(
        filter((userEvent) => {
          if (!userEvent || !this.spaceRepo.activeSpace) {
            return false;
          }
          const followingID = this.currentFollowModeState?.followingID;
          return (
            followingID === userEvent?.userId &&
            userEvent?.type === UserEventType.LOCATION &&
            userEvent.location?.vpt !== undefined
          );
        }),
      )
      .subscribe((userEvent) => {
        if (userEvent?.location) {
          const applyCentering =
            this.currentFollowModeState?.followingTabID !== this.userService.userUniqueHash;
          this.leaderFollowModesSharedUtilsService.updateVptUsingFollowingLocation(
            userEvent.location,
            applyCentering,
          );
        }
      });
  }

  private setupFollowingUserPresenceListener() {
    this.followingUserPresenceSubscription?.unsubscribe();
    this.followingUserPresenceSubscription = this.presenceRepository
      .getPresenceUsersData$(
        this.spaceRepo.activeSpaceId || '',
        PresenceType.SPACE_PRESENCE,
        ContextTypeEnum.SPACE,
        this.spaceRepo.activeSpaceCurrentRoomUid,
      )
      .subscribe((presence) => {
        const newFollowingUserOpenedTabs = this.getOpenedTabsForUser(
          this.currentFollowModeState?.followingID || '',
          presence,
        );
        const currentFollowingUserOpenedTabs = this._followingUserOpenedTabs$.value;
        this._followingUserOpenedTabs$.next(new Set<string>(newFollowingUserOpenedTabs));

        // following tab closed
        if (
          this.currentFollowModeState?.followingTabID &&
          !newFollowingUserOpenedTabs.has(this.currentFollowModeState?.followingTabID)
        ) {
          this.handleFollowingUserClosedTab(newFollowingUserOpenedTabs);
          return;
        }

        // new Tab opened
        for (const tabID of newFollowingUserOpenedTabs) {
          if (!currentFollowingUserOpenedTabs.has(tabID)) {
            this.handleFollowingUserOpenedNewTab(tabID);
            return;
          }
        }
      });
  }

  private handleFollowingUserClosedTab(newFollowingUserOpenedTabs: Set<string>) {
    if (newFollowingUserOpenedTabs.size === 0) {
      this.stopFollowing();
      return;
    }

    const newFollowingTabID = this.getMaxTabID(newFollowingUserOpenedTabs);
    this.startFollowing(this.currentFollowModeState?.followingID || '', newFollowingTabID);
  }

  private handleFollowingUserOpenedNewTab(tabID: string) {
    const followingUser = this.spaceRepo.activeSpace?.populatedUsers.find(
      (user) => user._id === this.currentFollowModeState?.followingID,
    );
    const notificationTitle = `${followingUser?.name || ''} ${this.translateService.instant(
      'has opened a new tab',
    )}`;
    const notificationDescription = this.translateService.instant(
      'You can follow the new tab or stay on this one.',
    );

    const notificationIconConfig: NotificationIconConfig = {
      type: NotificationIconTypes.SVG_ICON,
      background: IconBackground.INFO,
      assetName: 'tabs-icon-black',
      height: 18,
      width: 18,
    };

    const primaryActionButton: NotificationActionButton = {
      text: this.translateService.instant('Follow new tab'),
      icon: 'follow-icon-white',
      iconConfig: {
        type: IconTypes.svgIcon,
      },
      onClick: () => {
        this.startFollowing(this.currentFollowModeState?.followingID || '', tabID);
      },
      close: true,
    };

    const notificationData = new V2NotificationDataBuilder(INFOS.FOLLOWING_OPENED_NEW_TAB)
      .type(NotificationType.INFO)
      .style(ToasterPopupStyle.INFO)
      .title(notificationTitle)
      .description(notificationDescription)
      .icon(notificationIconConfig)
      .actionButtonsConfig({
        primary: primaryActionButton,
      })
      .allowActionsForObservers()
      .showProgressBar(false)
      .dismissable(true)
      .priority(600)
      .timeOut(10)
      .onActivateTick(true)
      .build();

    this.notificationToasterService.showNotification(notificationData, () => {
      this.newTabNotificationSubscription?.unsubscribe();
    });

    this.setNewTabNotificationSubscription(tabID);
  }

  private setNewTabNotificationSubscription(tabID: string) {
    this.newTabNotificationSubscription?.unsubscribe();
    this.newTabNotificationSubscription = this.followingUserOpenedTabs$.subscribe((tabs) => {
      if (!tabs.has(tabID)) {
        this.notificationToasterService.dismissNotificationsByCode([
          INFOS.FOLLOWING_OPENED_NEW_TAB,
        ]);
        this.newTabNotificationSubscription?.unsubscribe();
      }
    });
  }

  private setupImplicitCloseListener() {
    // if inspect mode is active, we don't need to setup the implicit close listener
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    this.cleanUpImplicitCloseListeners();

    this.implicitCloseSubscription.push(
      this.sessionSharedDataService.sessionView.current$.subscribe((sessionView) => {
        const sessionViewChanged =
          sessionView.view !== this.currentFollowModeState?.followingSpaceView;
        const fullScreenIDChanged =
          sessionView.sessionViewMetaData?.fullscreenId !==
          this.currentFollowModeState?.followingSessionViewMetaData?.fullscreenId;
        const expandedFullScreenUserChanged =
          sessionView.expandedFullScreenChange?.tileData?.userId !==
          this.currentFollowModeState?.followingExpandAndFullScreenChange?.tileData?.userId;
        const expandedFullScreenStateChanged =
          sessionView.expandedFullScreenChange?.fullscreen !==
          this.currentFollowModeState?.followingExpandAndFullScreenChange?.fullscreen;
        const expandedFullScreentrackChanged =
          sessionView.expandedFullScreenChange?.tileData?.trackType !==
          this.currentFollowModeState?.followingExpandAndFullScreenChange?.tileData?.trackType;

        if (
          sessionViewChanged ||
          fullScreenIDChanged ||
          expandedFullScreenUserChanged ||
          expandedFullScreenStateChanged ||
          expandedFullScreentrackChanged
        ) {
          this.implicitStopFollowing({
            sessionViewChanged,
            fullScreenIDChanged,
            expandedFullScreenUserChanged,
            expandedFullScreenStateChanged,
            expandedFullScreentrackChanged,
          });
        }
      }),
    );

    this.implicitCloseSubscription.push(
      this.spaceRepo.activeSpaceSelectedBoardUid$
        .pipe(distinctUntilChanged())
        .subscribe((activeBoardUid) => {
          const boardChanged = activeBoardUid !== this.currentFollowModeState?.followingFrameID;
          if (boardChanged) {
            this.implicitStopFollowing({
              boardChanged,
            });
          }
        }),
    );

    this.implicitCloseSubscription.push(
      this.sessionsVptService.internalVPTTransformChanges$.subscribe((vptTransformChanged) => {
        if (vptTransformChanged) {
          this.implicitStopFollowing({
            vptTransformChanged,
          });
        }
      }),
    );
  }

  implicitStopFollowing(implicitCloseReason: IImplicitCloseReason) {
    if (!this.isFollowing()) {
      return;
    }
    this.logFollowModeImplicitClose(implicitCloseReason);
    this.stopFollowing();
  }

  private logFollowModeImplicitClose(implicitCloseReason: IImplicitCloseReason) {
    let message = '[FollowMode] implicit stop following. Reason: ';
    if (implicitCloseReason.sessionViewChanged) {
      message += 'sessionViewChanged, ';
    }
    if (implicitCloseReason.fullScreenIDChanged) {
      message += 'fullScreenIDChanged, ';
    }
    if (implicitCloseReason.expandedFullScreenUserChanged) {
      message += 'expandedFullScreenUserChanged, ';
    }
    if (implicitCloseReason.expandedFullScreenStateChanged) {
      message += 'expandedFullScreenStateChanged, ';
    }
    if (implicitCloseReason.expandedFullScreentrackChanged) {
      message += 'expandedFullScreentrackChanged, ';
    }
    if (implicitCloseReason.boardChanged) {
      message += 'boardChanged, ';
    }
    if (implicitCloseReason.vptTransformChanged) {
      message += 'vptTransformChanged, ';
    }

    console.log(message);
  }

  stopFollowing() {
    console.log('[FollowMode] stop following');
    this.cleanUpFollowingListeners();
    this._followingUserOpenedTabs$.next(new Set<string>());
    this.realtimeSpaceService.service.modifyTemporaryUserMetadata({
      followingID: undefined,
      followingTabID: undefined,
      isFollowing: false,
    });
  }

  startLeading() {
    this.telemetryService.logBasicEvent('[FollowMode] start leading');
    console.log('[FollowMode] start leading');
    const frameUID = this.spaceRepo?.activeSpaceSelectedBoardUid;
    this.realtimeSpaceService.service.modifyTemporaryUserMetadata({
      isBeingFollowed: true,
      frameUID,
    });
    this.leaderFollowModesSharedUtilsService.updatePersistentLocation(frameUID);
  }

  stopLeading() {
    console.log('[FollowMode] stop leading');
    this.realtimeSpaceService.service.modifyTemporaryUserMetadata({ isBeingFollowed: false });
    this.setupVPTEvents(this.currentFollowModeState, {
      ...this.currentFollowModeState,
      followers: undefined,
    });
  }

  cleanService() {
    this.deactivateFollowMode();
    this.leaderFollowModesSharedUtilsService.cleanUpService();

    this.followModeStateSubscription?.unsubscribe();
    this.followersSubscription?.unsubscribe();
    this.joiningCallSubscription?.unsubscribe();
    this.cleanUpDeactivateFollowModeListeners();
    this.leaderModeSubscription?.unsubscribe();
    this.followModeEnabledSubscription?.unsubscribe();
    this.previousFollowModeState = undefined;
    this.currentFollowModeState = undefined;

    this.cleanUpLeadingListeners();
    this.cleanUpFollowingListeners();

    this.newTabNotificationSubscription?.unsubscribe();
  }

  private cleanUpLeadingListeners() {}

  private cleanUpFollowingListeners() {
    this.followingUpdatesSubscription?.unsubscribe();
    this.framesStateSubscription?.unsubscribe();
    this.followingLocationSubscription?.unsubscribe();
    this.followingUserPresenceSubscription?.unsubscribe();
    this.cleanUpImplicitCloseListeners();
  }

  private cleanUpImplicitCloseListeners() {
    this.implicitCloseSubscription?.forEach((subscription) => subscription.unsubscribe());
    this.implicitCloseSubscription = [];
  }

  private cleanUpDeactivateFollowModeListeners() {
    this.deactivateFollowModeSubscriptions?.forEach((subscription) => subscription.unsubscribe());
    this.deactivateFollowModeSubscriptions = [];
  }
}
